/**
 * AskPrashasta — Contextual Help Companion
 * Deterministic, config-driven. No AI in this iteration.
 * Shows contextual suggested questions + curated answers per journey step.
 */
import { useState, useEffect } from 'react';
import { ChevronRight, ChevronDown, Send, MessageCircle, X } from 'lucide-react';
import { PJ } from '../journey/journeyConfig';
import { HELP_TOPICS, HELP_EVENTS, trackHelpTopicViewed, findBestMatch } from './journeyHelpConfig';

// ── Embedded styles ───────────────────────────────────────────────────────────
const ASK_STYLES = `
  @keyframes askAnswerIn {
    from { opacity: 0; transform: translateY(-6px); max-height: 0; }
    to   { opacity: 1; transform: translateY(0);    max-height: 400px; }
  }
  .ask-answer-reveal { animation: askAnswerIn 0.25s cubic-bezier(0.22,1,0.36,1) both; }
  .ask-question-row:hover .ask-question-text { color: #201B19; }
  .ask-input:focus { outline: 2px solid #C9A15F; outline-offset: 0; }
  .ask-q-btn:hover  { background: rgba(201,161,95,0.06)!important; }
  .ask-q-btn:focus  { outline: 2px solid #C9A15F; outline-offset: 2px; }
  .ask-planner-btn:hover { background: #201B19!important; color: #FBF7F2!important; }
`;

// ── Sub-components ────────────────────────────────────────────────────────────
function SuggestedQuestion({ q, isActive, onClick }) {
  return (
    <div className="ask-question-row" style={{ borderBottom: `1px solid ${PJ.sand}` }}>
      <button
        data-testid={`ask-q-${q.id}`}
        className="ask-q-btn"
        onClick={onClick}
        style={{
          width: '100%', textAlign: 'left', background: 'none', border: 'none',
          cursor: 'pointer', padding: '11px 12px',
          borderRadius: '10px',
          display: 'flex', alignItems: 'flex-start', gap: '10px',
          transition: 'background 0.15s',
        }}
      >
        <span style={{ color: isActive ? PJ.ruby : PJ.gold, marginTop: '2px', flexShrink: 0, transition: 'color 0.15s' }}>
          {isActive ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        </span>
        <span
          className="ask-question-text"
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '0.82rem',
            color: isActive ? PJ.obsidian : PJ.slate,
            lineHeight: 1.5,
            fontWeight: isActive ? 500 : 400,
            transition: 'color 0.15s',
          }}
        >
          {q.question}
        </span>
      </button>

      {isActive && (
        <div
          className="ask-answer-reveal"
          data-testid={`ask-answer-${q.id}`}
          style={{
            margin: '0 12px 12px 34px',
            padding: '12px 14px',
            background: PJ.ivory,
            borderRadius: '12px',
            border: `1px solid ${PJ.sand}`,
            borderLeft: `3px solid ${PJ.gold}`,
          }}
        >
          <p style={{
            margin: 0,
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '0.82rem',
            color: PJ.obsidian,
            lineHeight: 1.65,
          }}>
            {q.answer}
          </p>
        </div>
      )}
    </div>
  );
}

// ── Main AskPrashasta component ───────────────────────────────────────────────
export function AskPrashasta({ currentStepId, onAskPlanner }) {
  const [activeId, setActiveId] = useState(null);
  const [freeText, setFreeText] = useState('');
  const [freeResult, setFreeResult] = useState(null); // null | { found, question?, answer? }

  const topics = HELP_TOPICS[currentStepId] || null;

  // Reset state when step changes
  useEffect(() => {
    setActiveId(null);
    setFreeText('');
    setFreeResult(null);
  }, [currentStepId]);

  if (!topics) {
    return (
      <div style={{ padding: '20px 0', textAlign: 'center' }}>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: PJ.slate, fontStyle: 'italic', margin: 0 }}>
          Need help? Ask your planner directly.
        </p>
        <button
          onClick={onAskPlanner}
          style={{
            marginTop: '14px',
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            background: PJ.ruby, color: PJ.white,
            border: 'none', borderRadius: '100px',
            padding: '10px 20px',
            fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.82rem',
            cursor: 'pointer',
          }}
        >
          <MessageCircle size={14} /> Ask My Planner
        </button>
      </div>
    );
  }

  const handleQuestionClick = (q) => {
    const next = activeId === q.id ? null : q.id;
    setActiveId(next);
    if (next) {
      trackHelpTopicViewed(q.id);
      // log analytic event (fire-and-forget)
      try { window._pj_analytics?.push({ event: HELP_EVENTS.TOPIC_SELECTED, topic: q.id, step: currentStepId }); } catch { }
    }
    setFreeText('');
    setFreeResult(null);
  };

  const handleFreeSubmit = () => {
    const trimmed = freeText.trim();
    if (!trimmed) return;
    try { window._pj_analytics?.push({ event: HELP_EVENTS.FREE_TEXT, text: trimmed, step: currentStepId }); } catch { }
    const match = findBestMatch(trimmed, topics);
    if (match) {
      setFreeResult({ found: true, question: match.question, answer: match.answer });
      setActiveId(null);
      trackHelpTopicViewed(match.id);
    } else {
      setFreeResult({ found: false });
      try { window._pj_analytics?.push({ event: HELP_EVENTS.UNANSWERED, text: trimmed, step: currentStepId }); } catch { }
    }
  };

  return (
    <div data-testid="ask-prashasta-panel">
      <style>{ASK_STYLES}</style>

      {/* Header */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: PJ.ruby, flexShrink: 0 }} />
          <p style={{
            margin: 0,
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: '1.15rem',
            fontWeight: 500,
            letterSpacing: '0.02em',
            color: PJ.obsidian,
          }}>
            Ask Prashasta
          </p>
        </div>
        <p style={{
          margin: '0 0 0 14px',
          fontFamily: "'DM Sans', sans-serif",
          fontSize: '0.75rem',
          color: PJ.slate,
          fontStyle: 'italic',
        }}>
          Not sure about something? We can help.
        </p>
      </div>

      {/* Step context label */}
      <p style={{
        margin: '0 0 10px',
        fontFamily: "'DM Sans', sans-serif",
        fontSize: '0.62rem',
        fontWeight: 700,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: PJ.gold,
      }}>
        {topics.stepLabel}
      </p>

      {/* Suggested questions */}
      <div
        data-testid="ask-suggested-questions"
        style={{
          background: PJ.white,
          borderRadius: '14px',
          border: `1px solid ${PJ.sand}`,
          overflow: 'hidden',
          marginBottom: '14px',
        }}
      >
        {topics.questions.map((q, i) => (
          <SuggestedQuestion
            key={q.id}
            q={q}
            isActive={activeId === q.id}
            onClick={() => handleQuestionClick(q)}
          />
        ))}
      </div>

      {/* Free text result — matched */}
      {freeResult?.found && (
        <div
          data-testid="ask-free-answer"
          className="ask-answer-reveal"
          style={{
            padding: '14px 16px',
            background: PJ.white,
            borderRadius: '14px',
            border: `1px solid ${PJ.sand}`,
            borderLeft: `3px solid ${PJ.ruby}`,
            marginBottom: '12px',
          }}
        >
          <p style={{ margin: '0 0 6px', fontFamily: "'DM Sans', sans-serif", fontSize: '0.75rem', fontWeight: 600, color: PJ.ruby }}>
            {freeResult.question}
          </p>
          <p style={{ margin: 0, fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: PJ.obsidian, lineHeight: 1.65 }}>
            {freeResult.answer}
          </p>
        </div>
      )}

      {/* Free text result — fallback */}
      {freeResult && !freeResult.found && (
        <div
          data-testid="ask-fallback"
          className="ask-answer-reveal"
          style={{
            padding: '14px 16px',
            background: `${PJ.blush}60`,
            borderRadius: '14px',
            border: `1px solid ${PJ.sand}`,
            marginBottom: '12px',
          }}
        >
          <p style={{ margin: '0 0 10px', fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: PJ.obsidian, lineHeight: 1.6 }}>
            That's a good question. Your Prashasta planner can help with this.
          </p>
          <button
            data-testid="ask-planner-btn"
            className="ask-planner-btn"
            onClick={() => {
              try { window._pj_analytics?.push({ event: HELP_EVENTS.PLANNER_HANDOFF, step: currentStepId }); } catch { }
              onAskPlanner();
            }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              background: PJ.obsidian, color: PJ.white,
              border: 'none', borderRadius: '100px',
              padding: '9px 18px',
              fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.8rem',
              cursor: 'pointer', transition: 'all 0.15s',
            }}
          >
            <MessageCircle size={13} />
            Ask My Planner
          </button>
        </div>
      )}

      {/* Free text input */}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <input
          data-testid="ask-free-input"
          className="ask-input"
          type="text"
          value={freeText}
          onChange={e => { setFreeText(e.target.value); setFreeResult(null); }}
          onKeyDown={e => { if (e.key === 'Enter') handleFreeSubmit(); }}
          placeholder="Ask something..."
          style={{
            flex: 1, padding: '10px 14px',
            fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem',
            background: PJ.ivory, border: `1px solid ${PJ.sand}`,
            borderRadius: '100px', color: PJ.obsidian,
            transition: 'border-color 0.15s',
          }}
        />
        <button
          data-testid="ask-submit-btn"
          onClick={handleFreeSubmit}
          disabled={!freeText.trim()}
          style={{
            width: '36px', height: '36px', borderRadius: '50%',
            background: freeText.trim() ? PJ.ruby : PJ.sand,
            border: 'none', cursor: freeText.trim() ? 'pointer' : 'default',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.15s', flexShrink: 0,
          }}
        >
          <Send size={13} style={{ color: PJ.white }} />
        </button>
      </div>
    </div>
  );
}

// ── Mobile Bottom Sheet wrapper ───────────────────────────────────────────────
export function AskPrashastaSheet({ isOpen, onClose, currentStepId, onAskPlanner }) {
  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          data-testid="ask-sheet-backdrop"
          onClick={onClose}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(32,27,25,0.45)',
            backdropFilter: 'blur(4px)',
            zIndex: 400,
          }}
        />
      )}

      {/* Sheet */}
      <div
        data-testid="ask-sheet"
        style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          background: PJ.ivory,
          borderRadius: '24px 24px 0 0',
          padding: '20px 20px 36px',
          zIndex: 410,
          maxHeight: '78vh',
          overflowY: 'auto',
          transform: isOpen ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.32s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: '0 -12px 40px rgba(32,27,25,0.16)',
        }}
      >
        {/* Sheet handle + close */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div style={{
            width: '36px', height: '4px', borderRadius: '100px',
            background: PJ.sand,
          }} />
          <button
            onClick={onClose}
            style={{
              background: PJ.sand, border: 'none', cursor: 'pointer',
              width: '28px', height: '28px', borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <X size={14} style={{ color: PJ.slate }} />
          </button>
        </div>

        <AskPrashasta
          currentStepId={currentStepId}
          onAskPlanner={() => { onClose(); onAskPlanner(); }}
        />
      </div>
    </>
  );
}
